<template>
  <div class="ov-root">

    <!-- Header -->
    <div class="ov-header">
      <div class="ov-header__left">
        <div class="ov-header__icon">
          <feather-icon icon="ActivityIcon" size="20" />
        </div>
        <div>
          <h2 class="ov-header__title">Visao Geral</h2>
          <p class="ov-header__sub">Ultimos usuarios cadastrados e metricas da plataforma</p>
        </div>
      </div>
      <button class="ov-refresh-btn" @click="fetchData" :disabled="loading">
        <feather-icon icon="RefreshCwIcon" size="14" :class="{ 'ov-spinning': loading }" />
        Atualizar
      </button>
    </div>

    <!-- Stats -->
    <div class="ov-stats">
      <div class="ov-stat">
        <div class="ov-stat__icon ov-stat__icon--blue">
          <feather-icon icon="UsersIcon" size="20" />
        </div>
        <div>
          <div class="ov-stat__value">{{ users.length }}</div>
          <div class="ov-stat__label">Usuarios</div>
        </div>
      </div>
      <div class="ov-stat">
        <div class="ov-stat__icon ov-stat__icon--green">
          <feather-icon icon="VideoIcon" size="20" />
        </div>
        <div>
          <div class="ov-stat__value">{{ totalVsls }}</div>
          <div class="ov-stat__label">Videos</div>
        </div>
      </div>
      <div class="ov-stat">
        <div class="ov-stat__icon ov-stat__icon--orange">
          <feather-icon icon="EyeIcon" size="20" />
        </div>
        <div>
          <div class="ov-stat__value">{{ formatNum(totalViews) }}</div>
          <div class="ov-stat__label">Visualizacoes</div>
        </div>
      </div>
      <div class="ov-stat">
        <div class="ov-stat__icon ov-stat__icon--teal">
          <feather-icon icon="MessageSquareIcon" size="20" />
        </div>
        <div>
          <div class="ov-stat__value">{{ totalChatsAtivos }}</div>
          <div class="ov-stat__label">Chats Ativos</div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ov-loading">
      <div class="ov-spinner" />
      <span>Carregando dados...</span>
    </div>

    <!-- Table -->
    <div v-else class="ov-table-card">
      <div class="ov-table-header">
        <h3 class="ov-table-title">Ultimos Usuarios Cadastrados</h3>
        <span class="ov-table-count">{{ users.length }} usuarios</span>
      </div>

      <div class="ov-table-wrap">
        <table class="ov-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th class="ov-center">Videos</th>
              <th class="ov-center">Views</th>
              <th class="ov-center">Plays</th>
              <th class="ov-center">Chat</th>
              <th>Plano</th>
              <th>Status</th>
              <th>Cadastro</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in users" :key="u.id" class="ov-row">
              <td>
                <div class="ov-user-cell">
                  <div class="ov-avatar" :style="{ background: getAvatarColor(u.id) }">
                    <img v-if="u.user_photo_url" :src="u.user_photo_url" class="ov-avatar-img" />
                    <span v-else>{{ getInitials(u) }}</span>
                  </div>
                  <div>
                    <div class="ov-user-name">{{ u.user_name }} {{ u.user_sobrenome || '' }}</div>
                    <div class="ov-user-email">{{ u.user_email }}</div>
                  </div>
                </div>
              </td>
              <td class="ov-center">
                <span class="ov-num">{{ u.total_vsls }}</span>
              </td>
              <td class="ov-center">
                <span class="ov-num">{{ formatNum(u.total_views) }}</span>
              </td>
              <td class="ov-center">
                <span class="ov-num">{{ formatNum(u.total_plays) }}</span>
              </td>
              <td class="ov-center">
                <span v-if="u.chats_ativos > 0" class="ov-badge ov-badge--on">
                  <span class="ov-badge-dot ov-badge-dot--on" />
                  {{ u.chats_ativos }} ativo{{ u.chats_ativos > 1 ? 's' : '' }}
                </span>
                <span v-else class="ov-badge ov-badge--off">
                  Sem chat
                </span>
              </td>
              <td>
                <span class="ov-plan">{{ u.plano || 'Free' }}</span>
              </td>
              <td>
                <span class="ov-status" :class="u.user_ativo ? 'ov-status--on' : 'ov-status--off'">
                  {{ u.user_ativo ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td>
                <span class="ov-date">{{ formatDate(u.user_created_at) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="users.length === 0" class="ov-empty">
        <feather-icon icon="UsersIcon" size="36" />
        <p>Nenhum usuario encontrado</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'AdminOverview',
  data() {
    return {
      users: [],
      loading: true,
    }
  },
  computed: {
    totalVsls() { return this.users.reduce((s, u) => s + (u.total_vsls || 0), 0) },
    totalViews() { return this.users.reduce((s, u) => s + (u.total_views || 0), 0) },
    totalChatsAtivos() { return this.users.reduce((s, u) => s + (u.chats_ativos || 0), 0) },
  },
  mounted() { this.fetchData() },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const { data } = await api.get('/usuarios/overview')
        this.users = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('Erro ao carregar overview:', e)
        this.users = []
      }
      this.loading = false
    },
    getInitials(u) {
      return ((u.user_name || '?')[0] + (u.user_sobrenome || '')[0]).toUpperCase()
    },
    getAvatarColor(id) {
      const colors = ['#E86A10', '#318feb', '#28c76f', '#ee566f', '#8b5cf6', '#0fb899']
      return colors[id % colors.length]
    },
    formatNum(n) {
      if (!n) return '0'
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return String(n)
    },
    formatDate(d) {
      if (!d) return '-'
      try {
        return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      } catch { return '-' }
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;

.ov-root { display: flex; flex-direction: column; gap: 16px; }

/* Header */
.ov-header {
  display: flex; align-items: center; justify-content: space-between;
  background: var(--dk-bg-card, rgba(255,255,255,0.04)); border: 1px solid var(--dk-border, rgba(255,255,255,0.08));
  border-radius: 14px; padding: 18px 22px;
  box-shadow: var(--dk-shadow-sm);
}
.ov-header__left { display: flex; align-items: center; gap: 14px; }
.ov-header__icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba($orange, 0.1); color: $orange;
  display: flex; align-items: center; justify-content: center;
}
.ov-header__title { font-size: 1.15rem; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); margin: 0; }
.ov-header__sub { font-size: 0.78rem; color: var(--dk-text-muted, rgba(255,255,255,0.4)); margin: 0; }

.ov-refresh-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: 1.5px solid var(--dk-border, rgba(255,255,255,0.08));
  background: var(--dk-bg-card, rgba(255,255,255,0.04)); color: var(--dk-text-secondary, rgba(255,255,255,0.55));
  font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
  &:hover { border-color: $orange; color: $orange; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.ov-spinning { animation: ov-spin 0.8s linear infinite; }
@keyframes ov-spin { to { transform: rotate(360deg); } }

/* Stats */
.ov-stats {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px;
}
.ov-stat {
  display: flex; align-items: center; gap: 14px;
  background: var(--dk-bg-card, rgba(255,255,255,0.04)); border: 1px solid var(--dk-border, rgba(255,255,255,0.08));
  border-radius: 14px; padding: 18px 20px;
  box-shadow: var(--dk-shadow-sm);
}
.ov-stat__icon {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ov-stat__icon--blue   { background: rgba(49,143,235,0.1); color: #318feb; }
.ov-stat__icon--green  { background: rgba(40,199,111,0.1); color: #28c76f; }
.ov-stat__icon--orange { background: rgba(232,106,16,0.1); color: $orange; }
.ov-stat__icon--teal   { background: rgba(15,184,153,0.1); color: #0fb899; }

.ov-stat__value { font-size: 1.5rem; font-weight: 800; color: var(--dk-text-primary, #e2e8f0); line-height: 1; }
.ov-stat__label { font-size: 0.75rem; font-weight: 600; color: var(--dk-text-muted, rgba(255,255,255,0.4)); margin-top: 2px; text-transform: uppercase; letter-spacing: 0.04em; }

/* Loading */
.ov-loading {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 60px 0; color: var(--dk-text-muted, rgba(255,255,255,0.4));
}
.ov-spinner {
  width: 24px; height: 24px; border-radius: 50%;
  border: 2.5px solid var(--dk-border, rgba(255,255,255,0.08)); border-top-color: $orange;
  animation: ov-spin 0.7s linear infinite;
}

/* Table card */
.ov-table-card {
  background: var(--dk-bg-card, rgba(255,255,255,0.04)); border: 1px solid var(--dk-border, rgba(255,255,255,0.08));
  border-radius: 14px; overflow: hidden;
  box-shadow: var(--dk-shadow-sm);
}
.ov-table-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; border-bottom: 1px solid var(--dk-border-light, rgba(255,255,255,0.06));
}
.ov-table-title { font-size: 0.95rem; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); margin: 0; }
.ov-table-count {
  font-size: 0.75rem; font-weight: 600; color: $orange;
  background: rgba($orange, 0.08); border-radius: 20px; padding: 3px 10px;
}

.ov-table-wrap { overflow-x: auto; }
.ov-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.ov-table thead tr { background: var(--dk-bg-inset, rgba(255,255,255,0.03)); }
.ov-table th {
  padding: 11px 16px; font-size: 0.7rem; font-weight: 700;
  color: var(--dk-text-muted, rgba(255,255,255,0.4)); text-transform: uppercase; letter-spacing: 0.06em;
  border-bottom: 1px solid var(--dk-border-light, rgba(255,255,255,0.06)); white-space: nowrap;
}
.ov-center { text-align: center; }

.ov-row {
  border-bottom: 1px solid var(--dk-border-light, rgba(255,255,255,0.05));
  transition: background 0.12s;
  &:hover { background: var(--dk-bg-card-hover, rgba(255,255,255,0.06)); }
  &:last-child { border-bottom: none; }
}
.ov-table td { padding: 12px 16px; vertical-align: middle; }

/* User cell */
.ov-user-cell { display: flex; align-items: center; gap: 10px; min-width: 200px; }
.ov-avatar {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 700; color: #fff; overflow: hidden;
}
.ov-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.ov-user-name { font-weight: 600; color: var(--dk-text-primary, #e2e8f0); font-size: 0.85rem; }
.ov-user-email { font-size: 0.75rem; color: var(--dk-text-muted, rgba(255,255,255,0.4)); }

/* Numbers */
.ov-num { font-weight: 700; color: var(--dk-text-primary, #e2e8f0); }

/* Badge chat */
.ov-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 600;
}
.ov-badge--on { background: rgba(40,199,111,0.1); color: #28c76f; }
.ov-badge--off { background: var(--dk-bg-inset, rgba(255,255,255,0.07)); color: var(--dk-text-muted, rgba(255,255,255,0.4)); }
.ov-badge-dot { width: 6px; height: 6px; border-radius: 50%; }
.ov-badge-dot--on { background: #28c76f; }

/* Plan */
.ov-plan {
  font-size: 0.78rem; font-weight: 600; color: var(--dk-text-secondary, rgba(255,255,255,0.5));
  background: var(--dk-bg-inset, rgba(255,255,255,0.07)); padding: 3px 9px; border-radius: 5px;
}

/* Status */
.ov-status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 600;
}
.ov-status--on { background: rgba(40,199,111,0.1); color: #28c76f; }
.ov-status--off { background: rgba(168,170,174,0.12); color: #82868b; }

/* Date */
.ov-date { font-size: 0.78rem; color: var(--dk-text-muted, rgba(255,255,255,0.4)); white-space: nowrap; }

/* Empty */
.ov-empty {
  text-align: center; padding: 48px 24px; color: var(--dk-text-muted, rgba(255,255,255,0.4));
  svg { opacity: 0.3; margin-bottom: 10px; }
  p { margin: 0; font-weight: 600; }
}

@media (max-width: 768px) {
  .ov-stats { grid-template-columns: repeat(2, 1fr); }
}
</style>
